import { InlineStack, Stack, Text } from '@customerio/pluma-components/react';
import { CopyableCode } from '@/components/copyable-code';
import { useAnalytics } from '@/services/analytics';
import type { ApiCredential } from '@/services/db/api-credential/entity';

/** What the copy event reports as `API Credential Copied`. */
const SITE_ID_LABEL = 'Site ID';
const API_KEY_LABEL = 'API Key';

type CredentialKeyPairProps = {
  credential: ApiCredential;
  /** `API Credential Type` on the copy event — which kind of credential is on screen. */
  credentialType: string;
  /** `Source` on the copy event — which page the copy happened on. */
  source: string;
};

type CredentialValueProps = {
  label: string;
  value: string;
  idNamespace: string;
  onCopy: () => void;
};

/**
 * `fs-block` keeps the whole field out of FullStory session recordings; the
 * value inside also carries `fs-exclude` through `CopyableCode`.
 */
function CredentialValue({ label, value, idNamespace, onCopy }: CredentialValueProps) {
  return (
    <Stack gap="050" className="fs-block">
      <Text text="product-label" as="div">
        {label}
      </Text>
      <CopyableCode value={value} onCopy={onCopy} idNamespace={idNamespace} truncate={false}>
        {value}
      </CopyableCode>
    </Stack>
  );
}

/**
 * A tracking credential's site ID and API key, side by side, each copyable.
 *
 * Shared by the connector pages that show the workspace's credentials inline
 * (the Customer.io API page, and the Segment source page once it migrates).
 */
export function CredentialKeyPair({ credential, credentialType, source }: CredentialKeyPairProps) {
  const { track } = useAnalytics();

  const trackCopy = (credentialCopied: string) => {
    void track('integration', 'apiCredentialsCopied', {
      'API Credential Type': credentialType,
      'API Credential Copied': credentialCopied,
      Source: source,
    });
  };

  return (
    <InlineStack gap="200" alignBlock="center">
      <CredentialValue
        label={SITE_ID_LABEL}
        value={credential.site_id}
        idNamespace="integrations.credentialSiteId"
        onCopy={() => trackCopy(SITE_ID_LABEL)}
      />
      <CredentialValue
        label={API_KEY_LABEL}
        value={credential.api_key}
        idNamespace="integrations.credentialApiKey"
        onCopy={() => trackCopy(API_KEY_LABEL)}
      />
    </InlineStack>
  );
}
